/**
 * Agent « visualisation des savoirs » (31/10/2026) : pour chaque module, 1 à 3 visuels de facilitation cognitive
 * (schéma de processus, cycle, frise, pyramide, matrice 2 × 2, tableau comparatif, carte conceptuelle, graphique sourcé),
 * dessinés par le serveur aux couleurs de l'organisme. Le formateur consulte, télécharge (PNG, SVG), retire, valide
 * (ce qui verrouille la génération du module) ; les visuels sont insérés dans le support PPTX du module.
 */
import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Download, PenTool, RefreshCw, Sparkles, Trash2, Wand2 } from "lucide-react";
import type { ModuleParcours } from "@/domaine/pedagogie/parcours";
import { api, type Formation, type ResultatVisuels, type VisuelFormation } from "../api";
import { Alerte, Bouton, Etiquette, useNotifier } from "../ui/base";
import { AlerteIaIndisponible, generationBloquee, MentionMoteur, MentionVerrou, ProgressionTache, useEtatIa, useGenerations, useTacheIa } from "./AssistantIa";
import { StyleDuSupport } from "./Charte";

const LIEN = "inline-flex items-center gap-1 text-[12.5px] font-medium text-accent underline-offset-4 hover:underline";

export function StudioVisuels({ formation }: { formation: Formation }) {
  const id = formation.id;
  const ia = useEtatIa();
  const notifier = useNotifier();
  const requetes = useQueryClient();
  const generations = useGenerations(id);
  const visuels = useQuery({ queryKey: ["visuels", id], queryFn: () => api.get<VisuelFormation[]>(`/formations/${id}/visuels`) });
  const rafraichir = () => Promise.all([requetes.invalidateQueries({ queryKey: ["visuels", id] }), requetes.invalidateQueries({ queryKey: ["generations", id] })]);
  const tache = useTacheIa<ResultatVisuels>(
    useCallback(
      (r: ResultatVisuels) => {
        void rafraichir();
        const ecartes = r.resultats.flatMap((x) => x.ecartes);
        notifier(r.echecs.length ? "danger" : "succes", `${r.resultats.reduce((a, x) => a + x.visuels.length, 0)} visuel(s) proposé(s)${ecartes.length ? ` — ${ecartes.length} graphique(s) écarté(s) faute de chiffres sourcés` : ""}${r.echecs.length ? ` — ${r.echecs.length} échec(s)` : ""}.`);
      },
      [id],
    ),
  );
  const valider = useMutation({ mutationFn: (module_index: number) => api.post(`/formations/${id}/visuels/valider`, { module_index }), onSuccess: async () => { await rafraichir(); notifier("succes", "Visuels validés : ils seront insérés dans le support PPTX du module."); } });
  const retirer = useMutation({ mutationFn: (v: string) => api.suppr(`/formations/${id}/visuels/${v}`), onSuccess: () => rafraichir() });
  const regenerer = useMutation({
    mutationFn: (o: { visuel: string; mode: "illustre" | "vectoriel" }) => api.post<{ note: string }>(`/formations/${id}/visuels/${o.visuel}/regenerer`, { mode: o.mode }),
    onSuccess: async (r) => {
      await Promise.all([rafraichir(), requetes.invalidateQueries({ queryKey: ["style-support", id] })]);
      notifier(r.note ? "danger" : "succes", r.note || "Visuel régénéré.");
    },
    onError: (e) => notifier("danger", e.message),
  });
  const modules = (formation.formation_modules as ModuleParcours[]).length ? (formation.formation_modules as ModuleParcours[]) : [{ titre: formation.formation_titre } as ModuleParcours];

  return (
    <div className="space-y-5">
      {!ia.chargement && !ia.disponible && <AlerteIaIndisponible />}
      <p className="text-[13px] text-encre-2">
        Pour chaque module, l'agent repère les notions les plus structurantes et choisit la forme qui correspond au savoir : une séquence devient un processus, une boucle un cycle, une comparaison un tableau, deux critères une matrice. Un graphique chiffré n'est produit que si ses chiffres figurent dans un extrait de la base de sources (la source est citée sous le graphique). Validez les visuels d'un module pour les figer : ils sont insérés dans son support PPTX.
      </p>
      <StyleDuSupport formationId={id} />
      <div className="flex flex-wrap items-center gap-3">
        <Bouton variante="primaire" icone={<Sparkles className="size-4" aria-hidden />} disabled={!ia.disponible || tache.enCours} onClick={() => tache.lancer({ type: "visuels", formation_id: id })}>
          Générer les visuels de tous les modules
        </Bouton>
        {ia.disponible && <MentionMoteur description={ia.description} />}
      </div>
      <ProgressionTache tache={tache} libelle="Visualisation des savoirs" />
      {tache.erreur && <Alerte ton="danger" titre={tache.erreur}>{tache.erreurs.length > 0 && <ul className="list-disc pl-4">{tache.erreurs.map((x) => <li key={x}>{x}</li>)}</ul>}</Alerte>}
      {tache.resultat && tache.resultat.resultats.some((r) => r.ecartes.length) && (
        <Alerte ton="attention" titre="Graphiques écartés">
          <ul className="list-disc pl-4">{tache.resultat.resultats.flatMap((r) => r.ecartes).map((x) => <li key={x}>{x}</li>)}</ul>
        </Alerte>
      )}

      {modules.map((m, i) => {
        const etat = generations.de(`visuels:${i + 1}`);
        const liste = visuels.data?.filter((v) => v.module_index === i) ?? [];
        const valides = liste.length > 0 && liste.every((v) => v.valide_le);
        return (
          <section key={i} className="rounded-md border border-trait p-4" data-testid={`visuels-module-${i + 1}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold">Module {i + 1} — {m.titre}</h3>
              <div className="flex flex-wrap items-center gap-2">
                <MentionVerrou etat={etat} />
                {!etat?.verrouille && (
                  <Bouton taille="sm" icone={<Sparkles className="size-3.5" aria-hidden />} disabled={!ia.disponible || tache.enCours || generationBloquee(etat)} onClick={() => tache.lancer({ type: "visuels", formation_id: id, module_index: i })}>
                    {liste.length ? "Régénérer" : "Générer"}
                  </Bouton>
                )}
                {liste.length > 0 && !valides && (
                  <Bouton taille="sm" variante="primaire" icone={<Check className="size-3.5" aria-hidden />} enCours={valider.isPending && valider.variables === i} onClick={() => valider.mutate(i)}>
                    Valider les visuels
                  </Bouton>
                )}
              </div>
            </div>
            {liste.length === 0 ? (
              <p className="mt-2 text-[13px] text-encre-3">Aucun visuel pour ce module.</p>
            ) : (
              <ul className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                {liste.map((v) => (
                  <li key={v.id} className="overflow-hidden rounded-md border border-trait bg-carte">
                    <img src={`/api/formations/${id}/visuels/${v.id}/vignette.png`} alt={v.description_alt} title={v.titre} className="block aspect-video w-full bg-white object-cover" loading="lazy" data-testid="vignette-visuel" />
                    <div className="space-y-1 border-t border-trait px-3 py-2">
                      <p className="flex flex-wrap items-center gap-1.5 text-[13px] font-medium">
                        <Etiquette>{v.type_libelle}</Etiquette>
                        <Etiquette ton={v.mode === "illustre" ? "accent" : "neutre"}>{v.mode_libelle}</Etiquette>
                        {v.style_nom && <Etiquette>{v.style_nom}</Etiquette>}
                        {v.valide_le && <Etiquette ton="accent">Validé</Etiquette>}
                        <span className="min-w-0 truncate">{v.notion}</span>
                      </p>
                      {v.note && <p className="text-[12px] text-encre-3">{v.note}</p>}
                      <p className="flex flex-wrap gap-3">
                        <a className={LIEN} href={`/api/formations/${id}/visuels/${v.id}/png`}><Download className="size-3.5" aria-hidden /> PNG</a>
                        <a className={LIEN} href={`/api/formations/${id}/visuels/${v.id}/svg`}><Download className="size-3.5" aria-hidden /> SVG</a>
                        {!v.valide_le && !etat?.verrouille && (
                          <>
                            <button type="button" className={LIEN} disabled={regenerer.isPending} onClick={() => regenerer.mutate({ visuel: v.id, mode: "illustre" })} title="Nouvelle illustration Gemini (1 image) avec le style du support">
                              {regenerer.isPending && regenerer.variables?.visuel === v.id ? <RefreshCw className="size-3.5 animate-spin" aria-hidden /> : <Wand2 className="size-3.5" aria-hidden />} Illustrer
                            </button>
                            <button type="button" className={LIEN} disabled={regenerer.isPending} onClick={() => regenerer.mutate({ visuel: v.id, mode: "vectoriel" })} title="Schéma vectoriel au style du support (gratuit)">
                              <PenTool className="size-3.5" aria-hidden /> Schéma
                            </button>
                          </>
                        )}
                        {!v.valide_le && (
                          <button type="button" className={`${LIEN} text-danger`} onClick={() => confirm("Retirer ce visuel ?") && retirer.mutate(v.id)}>
                            <Trash2 className="size-3.5" aria-hidden /> Retirer
                          </button>
                        )}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
